import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, unref as _unref, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "diaries" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("span", { class: "switch-label" }, "Toon gesloten weken:", -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("b", null, "Weet je zeker dat je deze week wilt sluiten?", -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", null, "Controleer of alles correct is ingevuld voor deze week.", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", null, "Week sluiten", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("label", null, "Project", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("label", null, "Week", -1)

import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import PageTitleBar from '@/components/PageTitleBar.vue'
import { EyeOutlined, FilePdfOutlined, CheckOutlined } from '@ant-design/icons-vue'
import dayjs, { Dayjs } from 'dayjs'
import Column from '@/types/column'
import { ls } from '@/ls'
import TableSettings from '@/types/tableSettings'
import { diaryStore } from '@/stores/diary'
import { notification } from 'ant-design-vue'
import DiarySignalingExportButton from '@/components/diary/DiarySignalingExportButton.vue'
import DiaryUnworkableDaysExportButton from '@/components/diary/DiaryUnworkableDaysExportButton.vue'
import FilterBox from '@/components/FilterBox.vue'
import { can } from '@/globalFunctions'


export default /*#__PURE__*/_defineComponent({
  __name: 'Diaries',
  setup(__props) {

const router = useRouter()

const tableSettings = ref<TableSettings>(ls.get('DiariesTableSettings') || {
  pagination: { current: 1, pageSize: 25 },
  showSizeChanger: false,
  pageSizeOptions: ['25', '50', '100'],
  activeFilters: null,
  sort: null,
  search: null,
  archived: false
})

const columns = ref([
  {
    title: 'Project',
    key: 'project.id',
    filters: [],
    sorter: false
  },
  {
    title: 'Week',
    key: 'period'
  },
  {
    title: 'Status',
    key: 'status'
  },
  {
    title: 'Actie\'s',
    key: 'actions'
  }
])

const showNewWeekModal = ref(false)
const projects = ref([])
const newWeek = ref<{ project_id:number|null, date:Dayjs|null }>({
  project_id: null,
  date: null
})

onMounted(() => {
  diaryStore().getDiaries(tableSettings.value)
  getProjects()
  getProjectFilter()
})

function getProjects () : void {
  if (!canEdit.value) return

  axios.get(process.env.VUE_APP_API_URL + '/projects?limit=1000').then((r) => {
    projects.value = r.data.data
  })
}

function getProjectFilter () : void {
  axios.post(process.env.VUE_APP_API_URL + '/filters/project/diaries', { textColumn: 'name', valueColumn: 'id' }).then((res) => {
    columns.value[0].filters = res.data
  })
}

function toggleNewWeekModal () : void {
  showNewWeekModal.value = !showNewWeekModal.value
  newWeek.value = {
    project_id: null,
    date: showNewWeekModal.value ? dayjs() : null
  }
}

function addNewWeek () : void {
  diaryStore().loading.saving = true
  axios.post(process.env.VUE_APP_API_URL + '/diaries', { project_id: newWeek.value.project_id, date: newWeek.value.date }).then(() => {
    diaryStore().getDiaries(tableSettings.value)
    toggleNewWeekModal()
  }).catch(e => {
    const alreadyExists = e.response.status === 409
    notification.error({
      message: alreadyExists ? 'Week bestaat al!' : 'Week NIET aangemaakt.',
      description: alreadyExists
        ? 'De week die u probeert aan te maken bestaat al.'
        : 'Er is iets mis gegaan. Probeer het later nog eens.'
    })
    toggleNewWeekModal()
  }).finally(() => {
    diaryStore().loading.saving = false
  })
}

function closeWeek (id:number) : void {
  axios.post(process.env.VUE_APP_API_URL + '/diaries/' + id + '/close').then(() => {
    diaryStore().getDiaries(tableSettings.value)
    notification.success({
      message: 'Week gesloten.',
      description: 'De week is succesvol afgesloten. Je ontvangt zo een mail met het weekrapport.',
      duration: 5
    })
  }).catch(() => {
    notification.error({
      message: 'Week NIET gesloten.',
      description: 'Er is iets mis gegaan. Probeer het later nog eens.'
    })
  })
}

function openPdf (url:string) : void {
  window.open(url, '_blank')
}

function filterItems (input:string, item:{ name:string }) {
  return item.name.toLowerCase().indexOf(input.toLowerCase()) >= 0
}

watch(() => tableSettings, () => {
  onTableSettingsChange()
}, { deep: true })

function handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
  // Remove key from filters if filter is an empty object.
  if (filters && Object.keys(filters).length) {
    Object.keys(filters).forEach((key) => {
      if (!filters[key] || !filters[key].length) {
        delete filters[key]

        columns.value.forEach((column:Column) => {
          if (column.key === key) {
            column.filteredValue = null
          }
        })
      }
    })
  }

  // Set pagination, filters and sorting on tableSettings.
  tableSettings.value.pagination = pagination
  tableSettings.value.activeFilters = filters
  tableSettings.value.sort = sort.order ? sort : null

  // Get list based on filters and search term.
  diaryStore().getDiaries(tableSettings.value)
}

function handleSearchChange (searchTerm:string) : void {
  tableSettings.value.search = searchTerm

  // Set page to 1 because of new search but retain filters and sorting.
  tableSettings.value.pagination.current = 1

  // Get list based on filters and search term.
  diaryStore().getDiaries(tableSettings.value)
}

function onTableSettingsChange () : void {
  // On tableSettings change, store them in local storage.
  ls.set('DiariesTableSettings', tableSettings.value)

  axios.post(process.env.VUE_APP_API_URL + '/users/constructionSiteApp/settings', {
    settingName: 'DiariesTableSettings',
    settingValue: tableSettings.value
  })

  // Set the current active filters on the columns.
  columns.value.forEach((column:Column) => {
    // Set active filters.
    if (tableSettings.value.activeFilters && column.key in tableSettings.value.activeFilters) {
      column.filteredValue = tableSettings.value.activeFilters[column.key]
    } else {
      column.filteredValue = null
    }

    // Set active sort order.
    if (tableSettings.value.sort && column.key === tableSettings.value.sort.columnKey) {
      column.defaultSortOrder = tableSettings.value.sort.order
      column.sortOrder = tableSettings.value.sort.order
    } else {
      column.sortOrder = false
    }
  })
}

const pagination = computed(() => {
  return {
    current: tableSettings.value.pagination.current,
    defaultPageSize: tableSettings.value.pagination.pageSize,
    pageSize: parseInt(diaryStore().diaries.meta.per_page),
    total: diaryStore().diaries.meta.total,
    showTotal: (total:number, range:number[]) => `Uitvoerdersdagboek ${range[0]} t/m ${range[1]} van de ${total}`,
    showSizeChanger: tableSettings.value.showSizeChanger,
    pageSizeOptions: tableSettings.value.pageSizeOptions
  }
})

function updateFilters (activeFilters:{[key: string]: number[]|string[]}) : void {
  tableSettings.value.activeFilters = activeFilters
  diaryStore().getDiaries(tableSettings.value)
}

function onRowClick (rowData:{ id:number }) : { onClick: () => void } {
  return {
    onClick: () => {
      const keyboard = window.event as KeyboardEvent
      if (can('construction_site_portal.diaries.edit')) {
        const route = router.resolve({ name: 'DiaryEdit', params: { diaryId: rowData.id.toString() } })
        window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
      } else if (can('construction_site_portal.diaries.view')) {
        const route = router.resolve({ name: 'DiaryShow', params: { diaryId: rowData.id.toString() } })
        window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
      }
    }
  }
}

const canEdit = computed(() => {
  return can('construction_site_portal.diaries.edit')
})

return (_ctx: any,_cache: any) => {
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_switch = _resolveComponent("a-switch")!
  const _component_a_input_search = _resolveComponent("a-input-search")!
  const _component_a_tag = _resolveComponent("a-tag")!
  const _component_a_tooltip = _resolveComponent("a-tooltip")!
  const _component_a_popconfirm = _resolveComponent("a-popconfirm")!
  const _component_a_table = _resolveComponent("a-table")!
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_col = _resolveComponent("a-col")!
  const _component_a_row = _resolveComponent("a-row")!
  const _component_a_date_picker = _resolveComponent("a-date-picker")!
  const _component_a_modal = _resolveComponent("a-modal")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(PageTitleBar, null, {
      title: _withCtx(() => [
        _createTextVNode("Uitvoerdersdagboek.")
      ]),
      buttons: _withCtx(() => [
        (_unref(canEdit))
          ? (_openBlock(), _createBlock(_component_a_button, {
              key: 0,
              type: "primary",
              onClick: toggleNewWeekModal
            }, {
              default: _withCtx(() => [
                _createTextVNode("Nieuwe week")
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (_unref(canEdit))
          ? (_openBlock(), _createBlock(DiarySignalingExportButton, {
              key: 1,
              projects: projects.value
            }, null, 8, ["projects"]))
          : _createCommentVNode("", true),
        (_unref(canEdit))
          ? (_openBlock(), _createBlock(DiaryUnworkableDaysExportButton, {
              key: 2,
              projects: projects.value
            }, null, 8, ["projects"]))
          : _createCommentVNode("", true),
        _createVNode(_component_a_switch, {
          checked: tableSettings.value.archived,
          "onUpdate:checked": _cache[0] || (_cache[0] = ($event: any) => ((tableSettings.value.archived) = $event)),
          onChange: _cache[1] || (_cache[1] = ($event: any) => (_unref(diaryStore)().getDiaries(tableSettings.value)))
        }, null, 8, ["checked"]),
        _createTextVNode(),
        _hoisted_2
      ]),
      search: _withCtx(() => [
        _createVNode(_component_a_input_search, {
          placeholder: "Zoeken..",
          style: {"width":"100%"},
          value: tableSettings.value.search,
          "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((tableSettings.value.search) = $event)),
          onSearch: handleSearchChange,
          "enter-button": "",
          "allow-clear": ""
        }, null, 8, ["value"])
      ]),
      _: 1
    }),
    _createVNode(FilterBox, {
      view: "diaries",
      "active-filters": tableSettings.value.activeFilters,
      columns: columns.value,
      onUpdate: updateFilters
    }, null, 8, ["active-filters", "columns"]),
    _createVNode(_component_a_table, {
      columns: columns.value,
      "data-source": _unref(diaryStore)().diaries.data,
      rowKey: "id",
      customRow: onRowClick,
      pagination: _unref(pagination),
      loading: _unref(diaryStore)().loading.list,
      onChange: handleTableChange
    }, {
      bodyCell: _withCtx(({ column, record }) => [
        (column.key === 'project.id')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createTextVNode(_toDisplayString(record.project.name) + " (" + _toDisplayString(record.project.reference) + ") ", 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'period')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createTextVNode("Week " + _toDisplayString(record.week) + ", " + _toDisplayString(record.year), 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'status')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              (record.status === 1)
                ? (_openBlock(), _createBlock(_component_a_tag, {
                    key: 0,
                    color: "#6fb34e"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode("Open")
                    ]),
                    _: 1
                  }))
                : (_openBlock(), _createBlock(_component_a_tag, {
                    key: 1,
                    color: "grey"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode("Gesloten")
                    ]),
                    _: 1
                  }))
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'actions')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              (!_unref(canEdit))
                ? (_openBlock(), _createBlock(_component_a_button, {
                    key: 0,
                    size: "small",
                    onClick: ($event: any) => (_unref(router).push({ name: 'DiaryShow', params: { diaryId: record.id } }))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(EyeOutlined))
                    ]),
                    _: 2
                  }, 1032, ["onClick"]))
                : _createCommentVNode("", true),
              (_unref(canEdit) && record.status === 1)
                ? (_openBlock(), _createBlock(_component_a_popconfirm, {
                    key: 1,
                    placement: "bottomRight",
                    "ok-text": "Ja",
                    "cancel-text": "Nee",
                    onConfirm: ($event: any) => (closeWeek(record.id))
                  }, {
                    title: _withCtx(() => [
                      _hoisted_3,
                      _hoisted_4,
                      _hoisted_5
                    ]),
                    default: _withCtx(() => [
                      _createVNode(_component_a_tooltip, { placement: "topRight" }, {
                        title: _withCtx(() => [
                          _hoisted_6
                        ]),
                        default: _withCtx(() => [
                          _createVNode(_component_a_button, {
                            size: "small",
                            style: {"margin-right":"8px"},
                            onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(CheckOutlined))
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 2
                  }, 1032, ["onConfirm"]))
                : _createCommentVNode("", true),
              (_unref(canEdit) && record.pdf)
                ? (_openBlock(), _createBlock(_component_a_button, {
                    key: 2,
                    size: "small",
                    style: {"margin-right":"8px"},
                    onClick: _withModifiers(($event: any) => (openPdf(record.pdf.url)), ["stop"])
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(FilePdfOutlined))
                    ]),
                    _: 2
                  }, 1032, ["onClick"]))
                : _createCommentVNode("", true)
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["columns", "data-source", "pagination", "loading"]),
    _createVNode(_component_a_modal, {
      title: "Nieuwe week toevoegen",
      "ok-text": "Toevoegen",
      visible: showNewWeekModal.value,
      "ok-button-props": { loading: _unref(diaryStore)().loading.saving, disabled: _unref(diaryStore)().loading.saving },
      onOk: addNewWeek,
      onCancel: toggleNewWeekModal
    }, {
      default: _withCtx(() => [
        _createVNode(_component_a_row, null, {
          default: _withCtx(() => [
            _createVNode(_component_a_col, { span: 24 }, {
              default: _withCtx(() => [
                _hoisted_7,
                _createVNode(_component_a_select, {
                  "filter-option": filterItems,
                  fieldNames: { label: 'name', value: 'id' },
                  options: projects.value,
                  value: newWeek.value.project_id,
                  "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((newWeek.value.project_id) = $event)),
                  "show-search": ""
                }, null, 8, ["options", "value"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_a_row, { class: "no-mb" }, {
          default: _withCtx(() => [
            _createVNode(_component_a_col, { span: 24 }, {
              default: _withCtx(() => [
                _hoisted_8,
                _createVNode(_component_a_date_picker, {
                  format: "DD-MM-YYYY",
                  picker: "week",
                  value: newWeek.value.date,
                  "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((newWeek.value.date) = $event))
                }, null, 8, ["value"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["visible", "ok-button-props"])
  ]))
}
}

})